"use client";

import { cn } from "@/lib/utils";
import Image from "next/image";
import { PortableText } from "@portabletext/react";
import { richTextComponent } from "@/sanity/sanityPortableText";
import { PageChallengeSchema } from "@/sanity/pages/PageChallengeTemplate";
import ChipSerif from "@/components/global/ui/ChipSerif";
import checkbox from "@/public/challenge/checkbox.svg";
import { motion } from "framer-motion";
import FAQList from "@/components/global/ui/FAQList";
import { PortableTextBlock } from "sanity";
import { PageAffiliateSchema } from "@/sanity/pages/PageAffiliateTemplate";
import iqicon from "@/public/affiliate/iqIcon.svg";
import { CTAButton } from "@/shadcn/ui/button";

export default function ExclusiveSection({
  data,
  className,
}: {
  data: PageAffiliateSchema;
  className?: string;
}) {
  return (
    <section
      className={cn("relative overflow-clip  px-[2rem] lg:px-0", className)}
    >
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[38.25rem] sm:w-[55rem] lg:w-[min(180rem,80%)] h-full flex justify-between opacity-50 lg:opacity-100">
        {Array.from({ length: 7 }).map((_, index) => (
          <div
            key={index}
            className={cn(
              "h-full border-l border-l-[#E5E5E5]",
              index >= 5 && "hidden lg:block",
            )}
          />
        ))}
      </div>

      <div className="w-[min(119.875rem,100%)] mx-auto flex flex-col items-start py-[4.5rem] lg:py-[11.25rem] relative bg-white border border-[#E5E5E5] px-[2rem] lg:px-[14.25rem]">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.3, ease: [0.25, 0.46, 0.45, 0.94] }}
        >
          {/*<ChipSerif text={data.exclusiveChip} />*/}
          <Image
            src={iqicon}
            alt="iqicon"
            className="!w-[8.625rem] mb-[2.5rem]"
          />
        </motion.div>

        {data.exclusiveTitle && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.3, ease: [0.25, 0.46, 0.45, 0.94] }}
            className="richtext mb-[5rem]"
          >
            <PortableText
              value={data.exclusiveTitle}
              components={richTextComponent}
            />
          </motion.div>
        )}
        {data.exclusiveContent && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.3, ease: [0.25, 0.46, 0.45, 0.94] }}
            className="richtext richtext-start prose-p:text-[2rem] lg:prose-p:text-[3rem]"
          >
            <PortableText
              value={data.exclusiveContent}
              components={richTextComponent}
            />
          </motion.div>
        )}

        <hr className="w-full border-t border-dashed border-[#10303733] mt-[5rem] mb-[4rem]" />

        <div className="flex flex-col w-full gap-[1.75rem]">
          {data?.exclusiveItems &&
            data?.exclusiveItems.map((item, index) => (
              <motion.div
                key={index}
                className="flex items-center w-full bg-[#E6EDE9] p-[2.25rem] rounded-[2rem] gap-[2.25rem] lg:px-[3rem] lg:gap-[3.25rem]"
                initial={{ opacity: 0, y: 40, filter: "blur(10px)" }}
                whileInView={{ opacity: 1, y: 0, filter: "blur(0px)" }}
                viewport={{ once: true, margin: "-100px" }}
                transition={{
                  duration: 0.3,
                  delay: index * 0.1,
                  ease: [0.25, 0.4, 0.25, 1],
                }}
              >
                <Image
                  src={item.icon}
                  alt=""
                  className="!w-[5.5rem] shrink-0"
                />
                <p className="m-0 text-[#103037]">{item.text}</p>
              </motion.div>
            ))}
          <CTAButton
            data={data.exclusiveCta}
            className="w-full mt-[3rem] lg:w-fit lg:min-w-[40rem]"
          />
        </div>
      </div>
    </section>
  );
}
